import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import RichTextEditor, { RichTextEditorHandle } from '../../../components/common/RichTextEditor';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import modalCancelIcon from '../../../assets/Icon/project/modal_cancel.png';
import cancelIcon from '../../../assets/Icon/project/cancel.png';
import loadingLottie from '../../../assets/Icon/lottie/Insider-loading.lottie';
import { useTranslation } from 'react-i18next';
import DSModal from '../../../components/designSystem/DSModal';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');
const STORY_IMAGE_LIMIT = 3;
const STORY_CONTENT_WIDTH_PX = 580;
const STORY_MEDIA_PREVIEW_HEIGHT_PX = 220;

interface CreatePostModalProps {
  isOpen: boolean;
  isEditMode?: boolean;
  currentStep: number;
  mediaType: 'image' | 'video' | 'none';
  previewUrl: string | null;
  videoUrl: string;
  postContent: string;
  previewHtml?: string;
  isLoading?: boolean;
  onClose: () => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNextStep: () => void;
  onPrevStep: () => void;
  onMediaTypeChange: (type: 'image' | 'video' | 'none') => void;
  onContentChange: (content: string) => void;
  onVideoUrlChange: (value: string) => void;
  onRemoveMainMedia: () => void;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({
  isOpen,
  isEditMode = false,
  currentStep,
  mediaType,
  previewUrl,
  videoUrl,
  postContent,
  previewHtml,
  isLoading = false,
  onClose,
  onFileSelect,
  onNextStep,
  onPrevStep,
  onMediaTypeChange,
  onContentChange,
  onVideoUrlChange,
  onRemoveMainMedia,
}) => {
  const { t } = useTranslation('translation');
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [videoUrlFocused, setVideoUrlFocused] = useState(false);
  const processingLabel = stripEllipsis(t('common.actions.processing'));
  const storySectionGuide = React.useMemo(
    () => t('page.postManagement.modal.storySectionGuide', {
      defaultValue: t('page.postManagement.modal.storySectionTitle'),
    }),
    [t],
  );

  const editorRef = useRef<RichTextEditorHandle>(null);
  const videoFileInputRef = useRef<HTMLInputElement | null>(null);
  const step2ScrollRef = useRef<HTMLDivElement | null>(null);

  // 대표 이미지는 고정 미리보기로만 표시하고, 본문(story-image)만 3장 제한에 포함
  const embeddedImageCount = React.useMemo(() => {
    const doc = new DOMParser().parseFromString(postContent, 'text/html');
    return doc.querySelectorAll('figure[data-embed="story-image"]').length;
  }, [postContent]);

  const prevEmbeddedImageCountRef = useRef(embeddedImageCount);

  const scrollStep2EditorIntoView = useCallback(() => {
    const scrollEl = step2ScrollRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;

    const pad = 48;
    const scrollRect = scrollEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const sel = window.getSelection();
    let focusTop = editorRect.top;
    let focusBottom = editorRect.bottom;
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const r = range.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        focusTop = r.top;
        focusBottom = r.bottom;
      } else {
        // 접힌 캐럿(0×0): 커서는 보통 본문 하단 → 에디터 하단 기준으로 스크롤
        focusTop = editorRect.top;
        focusBottom = editorRect.bottom;
      }
    }

    if (focusBottom > scrollRect.bottom - pad) {
      scrollEl.scrollTop += focusBottom - (scrollRect.bottom - pad);
    } else if (focusTop < scrollRect.top + pad) {
      scrollEl.scrollTop += focusTop - (scrollRect.top + pad);
    }
  }, []);

  const scrollStep2TailIntoView = useCallback(() => {
    const scrollEl = step2ScrollRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;
    const images = editorEl.querySelectorAll<HTMLElement>('figure[data-embed="story-image"]');
    const lastImage = images.length ? images[images.length - 1] : null;
    const anchorRect = lastImage?.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const visibleBottom = scrollRect.bottom - 96;
    const targetBottom = Math.max(anchorRect ? anchorRect.bottom + 24 : 0, editorRect.bottom);
    if (targetBottom > visibleBottom) {
      scrollEl.scrollTop += targetBottom - visibleBottom;
    }
  }, []);

  const focusStep2Editor = useCallback(() => {
    const timeoutIds: number[] = [];
    const rafIds: number[] = [];
    const run = () => {
      editorRef.current?.focusAtEnd();
      const rid = requestAnimationFrame(() => {
        if (step2ScrollRef.current) {
          step2ScrollRef.current.scrollTop = step2ScrollRef.current.scrollHeight;
        }
        scrollStep2EditorIntoView();
        scrollStep2TailIntoView();
        editorRef.current?.focusAtEnd();
      });
      rafIds.push(rid);
    };

    [0, 80, 180, 320, 520, 780].forEach((delay) => {
      const tid = window.setTimeout(() => {
        const rid = requestAnimationFrame(run);
        rafIds.push(rid);
      }, delay);
      timeoutIds.push(tid);
    });

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [scrollStep2EditorIntoView, scrollStep2TailIntoView]);

  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const html = document.documentElement;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      htmlOverflow: html.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.position = prev.bodyPosition;
      document.body.style.top = prev.bodyTop;
      document.body.style.left = prev.bodyLeft;
      document.body.style.right = prev.bodyRight;
      document.body.style.width = prev.bodyWidth;
      document.body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);
  
  useEffect(() => {
    if (isOpen) return;
    setExitConfirmOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || currentStep !== 2) return;
    return focusStep2Editor();
  }, [isOpen, currentStep, focusStep2Editor]);

  useEffect(() => {
    if (!isOpen || currentStep !== 2 || !isEditMode) return;
    const timeoutIds: number[] = [];
    const rafIds: number[] = [];
    const run = () => {
      if (step2ScrollRef.current) {
        step2ScrollRef.current.scrollTop = step2ScrollRef.current.scrollHeight;
      }
      editorRef.current?.focusAtEnd();
      const rid = requestAnimationFrame(() => {
        scrollStep2TailIntoView();
        editorRef.current?.focusAtEnd();
      });
      rafIds.push(rid);
    };
    [0, 120, 260, 420, 620, 900].forEach((delay) => {
      const tid = window.setTimeout(() => {
        const rid = requestAnimationFrame(run);
        rafIds.push(rid);
      }, delay);
      timeoutIds.push(tid);
    });
    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      rafIds.forEach((id) => cancelAnimationFrame(id));
    };
  }, [isOpen, currentStep, isEditMode, postContent, scrollStep2TailIntoView]);

  useEffect(() => {
    if (currentStep !== 2) {
      prevEmbeddedImageCountRef.current = embeddedImageCount;
      return;
    }
    if (!isOpen) return;
    if (embeddedImageCount > prevEmbeddedImageCountRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          editorRef.current?.focusAtEnd();
          scrollStep2EditorIntoView();
          scrollStep2TailIntoView();
        });
      });
    }
    prevEmbeddedImageCountRef.current = embeddedImageCount;
  }, [currentStep, embeddedImageCount, isOpen, scrollStep2EditorIntoView, scrollStep2TailIntoView]);

  const videoUrlTrimmed = videoUrl.trim();
  const videoUrlValid = React.useMemo(() => {
    if (!videoUrlTrimmed) return false;
    try {
      const url = new URL(videoUrlTrimmed);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }, [videoUrlTrimmed]);
  const showVideoUrlError = Boolean(videoUrlTrimmed) && !videoUrlValid && !previewUrl;
  const videoEmbedUrl = React.useMemo(() => {
    if (!videoUrlValid) return '';
    try {
      const url = new URL(videoUrlTrimmed);
      const host = url.hostname.replace('www.', '');
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (host.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  }, [videoUrlTrimmed, videoUrlValid]);
  const videoPreviewSrc = React.useMemo(() => {
    if (!videoUrlValid) return '';
    return videoEmbedUrl || videoUrlTrimmed;
  }, [videoEmbedUrl, videoUrlTrimmed, videoUrlValid]);

  const isStep1Complete = mediaType === 'none'
    || (mediaType === 'image' ? previewUrl !== null : (Boolean(previewUrl) || videoUrlValid));

  const isNextDisabled = currentStep === 1 && !isStep1Complete;
  const hasPreview = mediaType === 'image'
    ? Boolean(previewUrl)
    : mediaType === 'video'
      ? Boolean(previewUrl) || Boolean(videoPreviewSrc)
      : false;
  const hasPreviewSummary = mediaType === 'image'
    ? Boolean(previewUrl)
    : mediaType === 'video'
      ? Boolean(previewUrl) || Boolean(videoPreviewSrc)
      : false;

  if (!isOpen) return null;

  const isImageLimitReached = embeddedImageCount >= STORY_IMAGE_LIMIT;

  const handleRequestClose = () => {
    setExitConfirmOpen(true);
  };

  const handleConfirmExit = () => {
    setExitConfirmOpen(false);
    onClose();
  };

  const handleVideoAttach = () => {
    if (videoUrlValid) return;
    videoFileInputRef.current?.click();
  };

  return (
    <>
      <ModalOverlay>
        <ModalContent>
        <ModalHeader>
          <ModalTitle>{isEditMode ? t('page.postManagement.modal.title.edit') : t('page.postManagement.modal.title.create')}</ModalTitle>
          <CloseButton onClick={handleRequestClose} type="button">
            <img src={modalCancelIcon} alt={t('common.actions.close')} width={20} height={20} />
          </CloseButton>
        </ModalHeader>

        <StepIndicator>
          <Step active={currentStep === 1}>
            <StepNumber active={currentStep === 1}>1</StepNumber>
            <StepLabel active={currentStep === 1}>{t('page.postManagement.modal.steps.media')}</StepLabel>
          </Step>
          <StepLine />
          <Step active={currentStep === 2}>
            <StepNumber active={currentStep === 2}>2</StepNumber>
            <StepLabel active={currentStep === 2}>{t('page.postManagement.modal.steps.story')}</StepLabel>
          </Step>
          <StepLine />
          <Step active={currentStep === 3}>
            <StepNumber active={currentStep === 3}>3</StepNumber>
            <StepLabel active={currentStep === 3}>{t('page.postManagement.modal.steps.preview')}</StepLabel>
          </Step>
        </StepIndicator>

        {currentStep === 1 && (
          <Step1Body>
            <Step1SectionTitle>{t('page.postManagement.modal.mediaSectionTitle')}</Step1SectionTitle>
            
            <MediaTypeSelector>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="mediaType"
                  value="image"
                  checked={mediaType === 'image'}
                  onChange={() => onMediaTypeChange('image')}
                />
                <RadioLabel>{t('page.postManagement.modal.mediaType.image')}</RadioLabel>
              </RadioOption>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="mediaType"
                  value="video"
                  checked={mediaType === 'video'}
                  onChange={() => onMediaTypeChange('video')}
                />
                <RadioLabel>{t('page.postManagement.modal.mediaType.video')}</RadioLabel>
              </RadioOption>
              <RadioOption>
                <RadioInput
                  type="radio"
                  name="mediaType"
                  value="none"
                  checked={mediaType === 'none'}
                  onChange={() => onMediaTypeChange('none')}
                />
                <RadioLabel>{t('page.postManagement.modal.mediaType.none')}</RadioLabel>
              </RadioOption>
            </MediaTypeSelector>

            <FileUploadSection>
              {mediaType === 'video' ? (
                <>
                  <VideoUrlRow>
                    <VideoUrlField
                      $state={showVideoUrlError ? 'error' : videoUrlFocused ? 'focus' : videoUrlTrimmed ? 'complete' : 'default'}
                    >
                      <VideoUrlText>
                        {(videoUrlFocused || videoUrlTrimmed) && (
                          <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>
                        )}
                        <VideoUrlInput
                          placeholder={videoUrlFocused || videoUrlTrimmed ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                          value={videoUrl}
                          onChange={(e) => {
                            onVideoUrlChange(e.target.value);
                          }}
                          onFocus={() => setVideoUrlFocused(true)}
                          onBlur={() => setVideoUrlFocused(false)}
                        />
                      </VideoUrlText>
                      {videoUrlTrimmed ? (
                        <VideoUrlClear
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            onVideoUrlChange('');
                          }}
                          aria-label="clear"
                        >
                          ×
                        </VideoUrlClear>
                      ) : null}
                    </VideoUrlField>
                  </VideoUrlRow>
                  {showVideoUrlError ? <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError> : null}
                </>
              ) : null}

              {mediaType === 'image' ? (
                <FileInputLabel htmlFor="file-upload">
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    onChange={onFileSelect}
                    style={{ display: 'none' }}
                  />
                  <UploadText>{t('page.postManagement.modal.uploadHintImage')}</UploadText>
                  <UploadButton as="span">{t('common.actions.attach')}</UploadButton>
                </FileInputLabel>
              ) : mediaType === 'video' ? (
                <input
                  ref={videoFileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={onFileSelect}
                  style={{ display: 'none' }}
                />
              ) : null}
            </FileUploadSection>

            <PreviewArea hasImage={hasPreview}>
              {mediaType === 'none' ? (
                <PreviewPlaceholder>
                  <PlaceholderText>{t('page.projectSettings.media.empty')}</PlaceholderText>
                  <PlaceholderIcon>
                    <PreviewNoneIcon
                      xmlns="http://www.w3.org/2000/svg"
                      width="41"
                      height="41"
                      viewBox="0 0 41 41"
                      fill="none"
                      aria-hidden
                      focusable="false"
                    >
                      <path d="M14 16.0833H14.0208M26.5 16.0833H26.5208M14 26.5H26.5M1.5 20.25C1.5 22.7123 1.98498 25.1505 2.92726 27.4253C3.86953 29.7002 5.25065 31.7672 6.99175 33.5082C8.73285 35.2493 10.7998 36.6305 13.0747 37.5727C15.3495 38.515 17.7877 39 20.25 39C22.7123 39 25.1505 38.515 27.4253 37.5727C29.7002 36.6305 31.7672 35.2493 33.5082 33.5082C35.2493 31.7672 36.6305 29.7002 37.5727 27.4253C38.515 25.1505 39 22.7123 39 20.25C39 17.7877 38.515 15.3495 37.5727 13.0747C36.6305 10.7998 35.2493 8.73285 33.5082 6.99175C31.7672 5.25065 29.7002 3.86953 27.4253 2.92726C25.1505 1.98498 22.7123 1.5 20.25 1.5C17.7877 1.5 15.3495 1.98498 13.0747 2.92726C10.7998 3.86953 8.73285 5.25065 6.99175 6.99175C5.25065 8.73285 3.86953 10.7998 2.92726 13.0747C1.98498 15.3495 1.5 17.7877 1.5 20.25Z" stroke="#BCBCBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </PreviewNoneIcon>
                  </PlaceholderIcon>
                </PreviewPlaceholder>
              ) : mediaType === 'video' && videoPreviewSrc ? (
                <PreviewIframe
                  src={videoPreviewSrc}
                  title="video preview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : mediaType === 'video' ? (
                <PreviewPlaceholder>
                  <PlaceholderText>{t('page.projectSettings.media.linkPreviewHint')}</PlaceholderText>
                  <PlaceholderIcon>
                    <PreviewVideoIcon
                      xmlns="http://www.w3.org/2000/svg"
                      width="70"
                      height="70"
                      viewBox="0 0 70 70"
                      fill="none"
                      aria-hidden
                      focusable="false"
                    >
                      <path d="M29.1665 24.7915L46.6665 34.9998L29.1665 45.2082V24.7915Z" fill="#BCBCBC" />
                      <path d="M35 14.5835C61.25 14.5835 61.25 14.5835 61.25 35.0002C61.25 55.4168 61.25 55.4168 35 55.4168C8.75 55.4168 8.75 55.4168 8.75 35.0002C8.75 14.5835 8.75 14.5835 35 14.5835Z" stroke="#BCBCBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </PreviewVideoIcon>
                  </PlaceholderIcon>
                </PreviewPlaceholder>
              ) : previewUrl ? (
                <>
                  {mediaType === 'image' ? (
                    <PreviewImage src={previewUrl} alt={t('page.postManagement.modal.previewAlt')} />
                  ) : (
                    <PreviewVideo src={previewUrl} controls />
                  )}
                  <RemoveMediaButton onClick={onRemoveMainMedia} type="button">
                    <img src={cancelIcon} alt={t('common.actions.delete')} width={32} height={32} />
                  </RemoveMediaButton>
                </>
              ) : (
                <PreviewPlaceholder>
                  <PlaceholderText>{t('page.postManagement.modal.previewHintImage')}</PlaceholderText>
                  <PlaceholderIcon>
                    <img src="/Image/posts/intro_image.png" alt="placeholder" width="60" height="60" />
                  </PlaceholderIcon>
                </PreviewPlaceholder>
              )}
            </PreviewArea>
          </Step1Body>
        )}

        {currentStep === 2 && (
          <Step2Container>
            <Step2Header>
              <Step2ContentColumn>
                <Step2StoryTitle>{storySectionGuide}</Step2StoryTitle>
              </Step2ContentColumn>
            </Step2Header>
            
            <Step2ScrollArea ref={step2ScrollRef}>
              <Step2ContentColumn>
                <RichTextEditor
                  ref={editorRef}
                  value={postContent}
                  onChange={onContentChange}
                  placeholder={t('page.postManagement.modal.storyPlaceholder')}
                  pictureIconSrc="/Image/posts/photoIcon.png"
                  maxImages={STORY_IMAGE_LIMIT}
                  imageCount={embeddedImageCount}
                  showToolbar={false}
                  wrapMarginTopPx={4}
                  previewHeightPx={STORY_MEDIA_PREVIEW_HEIGHT_PX}
                  pinnedImageSrc={mediaType === 'image' && previewUrl ? previewUrl : undefined}
                  pinnedVideoEmbedUrl={mediaType === 'video' && videoPreviewSrc ? videoPreviewSrc : undefined}
                  pinnedInsideEditor={true}
                  onFocus={() => {
                    requestAnimationFrame(() => {
                      scrollStep2EditorIntoView();
                      scrollStep2TailIntoView();
                    });
                  }}
                  onImageInserted={() => {
                    requestAnimationFrame(() => {
                      editorRef.current?.focusAtEnd();
                      requestAnimationFrame(() => {
                        scrollStep2EditorIntoView();
                        scrollStep2TailIntoView();
                        requestAnimationFrame(() => {
                          editorRef.current?.focusAtEnd();
                        });
                      });
                    });
                  }}
                />
              </Step2ContentColumn>
            </Step2ScrollArea>
            
            <AddPhotoButton>
              <AddPhotoTrigger
                type="button"
                onClick={() => editorRef.current?.openImagePicker()}
                disabled={isImageLimitReached}
              >
                <img src="/Image/posts/photoIcon.png" alt={t('page.postManagement.modal.photoAlt')} width={28} height={28} />
                {t('page.postManagement.modal.addPhoto', { count: embeddedImageCount })}
              </AddPhotoTrigger>
            </AddPhotoButton>
          </Step2Container>
        )}

        {currentStep === 3 && (
          <Step2Container>
            <Step2Header>
              <Step2ContentColumn>
                <Step2StoryTitle>{t('page.postManagement.modal.previewSectionTitle')}</Step2StoryTitle>
              </Step2ContentColumn>
            </Step2Header>
            
            <Step2ScrollArea>
              <Step2ContentColumn>
                {hasPreviewSummary && (
                  <Step2PreviewArea>
                    {mediaType === 'image' && previewUrl ? (
                      <PreviewImage src={previewUrl} alt={t('page.postManagement.modal.previewAlt')} />
                    ) : mediaType === 'video' && videoPreviewSrc ? (
                      <PreviewIframe
                        src={videoPreviewSrc}
                        title="video preview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : mediaType === 'video' && previewUrl ? (
                      <PreviewVideo src={previewUrl} controls />
                    ) : null}
                    {mediaType === 'image' && previewUrl && <MainImageBadge>{t('page.postManagement.modal.mainBadge')}</MainImageBadge>}
                  </Step2PreviewArea>
                )}
                
                <PreviewContent>
                  <SafeHtmlViewer html={previewHtml ?? postContent} />
                </PreviewContent>
              </Step2ContentColumn>
            </Step2ScrollArea>
          </Step2Container>
        )}

        <ModalFooter>
          <FooterRightButtons>
            {currentStep > 1 && (
              <BackButton onClick={onPrevStep} disabled={isLoading}>{t('common.actions.back')}</BackButton>
            )}
            <NextModalButton
              onClick={onNextStep}
              disabled={isNextDisabled || isLoading}
              $loading={isLoading}
              aria-busy={isLoading}
              aria-label={isLoading ? processingLabel : undefined}
            >
              {isLoading ? (
                <LoadingIcon
                  src={loadingLottie}
                  autoplay
                  loop
                  aria-hidden
                  style={{ width: 12, height: 12 }}
                />
              ) : (
                currentStep === 3 ? t('common.actions.complete') : t('common.actions.next')
              )}
            </NextModalButton>
          </FooterRightButtons>
        </ModalFooter>
        </ModalContent>
      </ModalOverlay>

      <DSModal
        open={exitConfirmOpen}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmOpen(false)}
        actions={[
          { label: t('common.exitConfirm.leave'), variant: 'secondary', onClick: handleConfirmExit },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmOpen(false) },
        ]}
      />
    </>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  overscroll-behavior: none;
`;

const ModalContent = styled.div`
  width: 760px;
  max-width: 100%;
  max-height: min(834px, calc(100vh - 32px));
  margin: 0;
  flex-shrink: 1;
  min-height: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  overscroll-behavior: contain;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 12px;
  background: #F8F8F8;
`;

const ModalTitle = styled.h2`
  ${({ theme }) => theme.typography.head.semiBold24}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  img {
    display: block;
    object-fit: contain;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
`;

const Step = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 27.68px;
  flex-shrink: 0;
`;

const StepNumber = styled.div<{ active: boolean }>`
  width: 27.68px;
  height: 27.68px;
  border-radius: 50%;
  background: ${({ active }) => active ? '#2BC2E1' : '#DFDFDF'};
  color: ${({ theme }) => theme.colors.grayScale[0]};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.contents.regular20}
  margin-bottom: 10px;
`;

const StepLabel = styled.span<{ active: boolean }>`
  ${({ theme }) => theme.typography.contents.regular16}
  color: ${({ active, theme }) => active ? theme.colors.primaryBlue[40] : '#DFDFDF'};
  white-space: nowrap;
  text-align: center;
`;

const StepLine = styled.div`
  width: 166px;
  height: 2px;
  background: ${({ theme }) => theme.colors.grayScale[20]};
  margin-top: 13.84px;
`;

const ModalBody = styled.div`
  padding: 8px 24px 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  @media (max-width: 800px) {
    padding: 20px 24px;
  }
`;

const Step1Body = styled(ModalBody)`
  padding: 0px 99px 20px;

  @media (max-width: 1366px) {
    padding: 0px 72px 20px;
  }

  @media (max-width: 800px) {
    padding: 20px 24px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px 0;
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const Step1SectionTitle = styled(SectionTitle)`
  white-space: nowrap;
  word-break: keep-all;
`;

const MediaTypeSelector = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 12px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const RadioInput = styled.input`
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: 1.5px solid #E5E5E5;
  border-radius: 50%;
  outline: none;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2bc2e1;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.15s ease;
  }

  &:checked::after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const RadioLabel = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin-right: 25px;
`;

const FileUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;

  &:empty {
    margin-bottom: 0;
  }
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UploadText = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[50]};
  flex: 1;
  padding: 0 12px;
  background: ${({ theme }) => theme.colors.grayScale[10]};
  border-radius: 4px;
  height: 56px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const UploadButton = styled.button`
  width: 130px;
  height: 56px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[40]};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  ${({ theme }) => theme.typography.button.regular20}
  color: ${({ theme }) => theme.colors.grayScale[80]};
  text-align: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const VideoUrlRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: 6px;
  border: 2px solid
    ${({ $state }) =>
      $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: 10px 12px;
  min-height: 56px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
  }
`;

const VideoUrlClear = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-top: -10px;
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  width: 130px;
  height: 56px;
  border-radius: 4px;
  border: 1px solid #dfdfdf;
  background: #fff;
  color: #404040;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PreviewArea = styled.div<{ hasImage: boolean }>`
  width: 100%;
  height: 220px;
  background: #F0F0F0;
  border-radius: 8px;
  border: ${({ hasImage }) => hasImage ? '3px solid #2BC2E1' : 'none'};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const PlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
`;

const PreviewVideoIcon = styled.svg`
  width: 70px;
  height: 70px;
`;

const PreviewNoneIcon = styled.svg`
  width: 41px;
  height: 41px;
`;

const PlaceholderText = styled.span`
  ${({ theme }) => theme.typography.contents.regular20}
  color: ${({ theme }) => theme.colors.grayScale[50]};
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 24px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  flex-shrink: 0;
  @media (max-width: 800px) {
    padding: 16px 24px 20px;
  }
`;

const FooterRightButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const BackButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  width: 130px;
  height: 54px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[40]};
  background: #FFFFFF;
  ${({ theme }) => theme.typography.button.regular20}
  color: #676767;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const NextModalButton = styled.button<{ disabled?: boolean; $loading?: boolean }>`
  display: flex;
  width: 130px;
  height: 54px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  border: none;
  background: ${({ disabled, $loading }) => ($loading ? '#EBFCFF' : disabled ? '#DFDFDF' : '#026478')};
  ${({ theme }) => theme.typography.button.regular20}
  color: ${({ disabled, $loading }) => ($loading ? '#2BC2E1' : disabled ? '#BCBCBC' : '#FFF')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;

const Step2Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const Step2StoryTitle = styled(SectionTitle)`
  margin: 0 0 4px 0;
  white-space: pre-line;
  line-height: 1.5;
  @media (max-width: 1366px) {
    margin-bottom: 4px;
  }
`;

const Step2Header = styled.div`
  padding: 0cqh 24px 1px;
  flex-shrink: 0;
  @media (max-width: 1366px) {
    padding: 0px 24px 1px;
  }
  @media (max-width: 800px) {
    padding: 8px 24px 4px;
  }
`;

const Step2ContentColumn = styled.div`
  width: min(100%, ${STORY_CONTENT_WIDTH_PX}px);
  margin: 0 auto;
`;

const Step2ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 24px;
  @media (max-width: 800px) {
    padding: 0 24px;
  }
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grayScale[10]};
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grayScale[40]};
    border-radius: 4px;
  }
`;

const Step2PreviewArea = styled.div`
  width: 100%;
  height: 220px;
  background: #F0F0F0;
  border-radius: 8px;
  border: 3px solid #2BC2E1;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  flex-shrink: 0;
  position: relative;
`;

const PreviewContent = styled.div`
  width: 100%;
  min-height: 300px;
  ${({ theme }) => theme.typography.contents.regular20}
  color: #17171B;
  white-space: pre-wrap;
  word-break: break-word;
`;

const MainImageBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 64px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.primaryBlue[40]};
  color: ${({ theme }) => theme.colors.grayScale[0]};
  ${({ theme }) => theme.typography.caption.bold14}
  z-index: 10;
`;

const RemoveMediaButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  cursor: pointer;
  z-index: 10;
  img {
    display: block;
    object-fit: contain;
  }
`;

const AddPhotoButton = styled.div`
  padding: 20px 0;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

const AddPhotoTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 353px;
  max-width: 100%;
  height: 54px;
  box-sizing: border-box;
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryBlue[10]};
  ${({ theme }) => theme.typography.button.medium20}
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  cursor: pointer;
  padding: 0 16px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  img {
    flex-shrink: 0;
    object-fit: contain;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryBlue[15]};
    border-color: ${({ theme }) => theme.colors.primaryBlue[50]};
    color: ${({ theme }) => theme.colors.primaryBlue[50]};
  }

  &:active:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryBlue[20]};
    border-color: ${({ theme }) => theme.colors.primaryBlue[60]};
    color: ${({ theme }) => theme.colors.primaryBlue[60]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default CreatePostModal;

